import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../home/components/Footer';

interface ServiceStatus {
  name: string;
  category: string;
  status: 'operational' | 'degraded' | 'outage';
  uptime: string;
  responseTime: string;
  lastChecked: string;
}

export default function StatusPage() {
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    notifications: {
      incidents: true,
      maintenance: true,
      updates: false
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
      setCurrentTime(new Date());
    }, 1000); // 每秒更新一次

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const services: ServiceStatus[] = [
    {
      name: 'Adobe PDF Library SDK',
      category: '核心产品',
      status: 'operational',
      uptime: '99.99%',
      responseTime: '45ms',
      lastChecked: '刚刚'
    },
    {
      name: 'Adobe PDF Converter',
      category: '核心产品',
      status: 'operational',
      uptime: '99.98%',
      responseTime: '52ms',
      lastChecked: '刚刚'
    },
    {
      name: 'PDF表单扩展SDK',
      category: '核心产品',
      status: 'operational',
      uptime: '99.97%',
      responseTime: '48ms',
      lastChecked: '刚刚'
    },
    {
      name: 'PDF REST Container',
      category: '核心产品',
      status: 'operational',
      uptime: '99.99%',
      responseTime: '38ms',
      lastChecked: '刚刚'
    },
    {
      name: 'OCR文本识别',
      category: '智能服务',
      status: 'operational',
      uptime: '99.96%',
      responseTime: '120ms',
      lastChecked: '刚刚'
    },
    {
      name: 'pdfAssistant AI',
      category: 'AI服务',
      status: 'operational',
      uptime: '99.95%',
      responseTime: '280ms',
      lastChecked: '刚刚'
    },
    {
      name: '文档AI智能分析',
      category: 'AI服务',
      status: 'operational',
      uptime: '99.94%',
      responseTime: '310ms',
      lastChecked: '刚刚'
    },
    {
      name: '文档数据结构化',
      category: '数据服务',
      status: 'operational',
      uptime: '99.98%',
      responseTime: '95ms',
      lastChecked: '刚刚'
    },
    {
      name: '文档权限管控',
      category: '安全服务',
      status: 'operational',
      uptime: '99.99%',
      responseTime: '42ms',
      lastChecked: '刚刚'
    },
    {
      name: '表单服务',
      category: '业务服务',
      status: 'operational',
      uptime: '99.97%',
      responseTime: '68ms',
      lastChecked: '刚刚'
    },
    {
      name: '电子签署',
      category: '业务服务',
      status: 'operational',
      uptime: '99.98%',
      responseTime: '75ms',
      lastChecked: '刚刚'
    },
    {
      name: 'API网关',
      category: '基础设施',
      status: 'operational',
      uptime: '99.99%',
      responseTime: '28ms',
      lastChecked: '刚刚'
    },
    {
      name: '数据库服务',
      category: '基础设施',
      status: 'operational',
      uptime: '99.99%',
      responseTime: '15ms',
      lastChecked: '刚刚'
    },
    {
      name: 'CDN服务',
      category: '基础设施',
      status: 'operational',
      uptime: '99.99%',
      responseTime: '22ms',
      lastChecked: '刚刚'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return 'text-green-600 bg-green-100';
      case 'degraded':
        return 'text-yellow-600 bg-yellow-100';
      case 'outage':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'operational':
        return '正常运行';
      case 'degraded':
        return '性能下降';
      case 'outage':
        return '服务中断';
      default:
        return '未知';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational':
        return 'ri-checkbox-circle-fill';
      case 'degraded':
        return 'ri-error-warning-fill';
      case 'outage':
        return 'ri-close-circle-fill';
      default:
        return 'ri-question-fill';
    }
  };

  const operationalCount = services.filter(s => s.status === 'operational').length;
  const totalCount = services.length;

  const groupedServices = services.reduce((acc, service) => {
    if (!acc[service.category]) {
      acc[service.category] = [];
    }
    acc[service.category].push(service);
    return acc;
  }, {} as Record<string, ServiceStatus[]>);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new FormData(formElement);
      
      const response = await fetch('https://readdy.ai/api/form/d5ri7q2hs78ip5370ib0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSend as any).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          setShowSubscriptionModal(false);
          setSubmitStatus('idle');
          setFormData({
            email: '',
            notifications: {
              incidents: true,
              maintenance: true,
              updates: false
            }
          });
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCheckboxChange = (key: string) => {
    setFormData(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: !prev.notifications[key as keyof typeof prev.notifications]
      }
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 to-cyan-600 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <div className="relative inline-flex items-center justify-center w-20 h-20 mb-6">
              <div className="absolute inset-0 bg-white/20 rounded-full animate-ping"></div>
              <div className="absolute inset-0 bg-white/30 rounded-full animate-pulse"></div>
              <div className="relative w-20 h-20 bg-white/40 rounded-full flex items-center justify-center">
                <i className="ri-checkbox-circle-fill text-5xl animate-pulse"></i>
              </div>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              系统状态
            </h1>
            <p className="text-xl lg:text-2xl text-teal-100 mb-4">
              所有系统运行正常
            </p>
            <p className="text-teal-200">
              最后更新：{lastUpdate.toLocaleString('zh-CN')}
            </p>
          </div>
        </div>
      </section>

      {/* Overall Status */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 mb-1">服务状态</p>
                  <p className="text-3xl font-bold text-green-600">{operationalCount}/{totalCount}</p>
                </div>
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill text-3xl text-teal-600"></i>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2">所有服务正常运行</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 mb-1">平均正常运行时间</p>
                  <p className="text-3xl font-bold text-gray-900">99.97%</p>
                </div>
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center">
                  <i className="ri-time-line text-3xl text-cyan-600"></i>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2">过去30天</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 mb-1">平均响应时间</p>
                  <p className="text-3xl font-bold text-gray-900">85ms</p>
                </div>
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
                  <i className="ri-speed-line text-3xl text-emerald-600"></i>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2">全球平均</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Status */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">服务详情</h2>
          
          <div className="space-y-8">
            {Object.entries(groupedServices).map(([category, categoryServices]) => (
              <div key={category}>
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="w-1 h-6 bg-blue-600 rounded-full mr-3"></span>
                  {category}
                </h3>
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  {categoryServices.map((service, index) => (
                    <div 
                      key={service.name}
                      className={`p-6 ${index !== categoryServices.length - 1 ? 'border-b border-gray-200' : ''} hover:bg-gray-50 transition-colors`}
                    >
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4 flex-1 min-w-[200px]">
                          <div className={`w-3 h-3 rounded-full ${service.status === 'operational' ? 'bg-green-500' : service.status === 'degraded' ? 'bg-yellow-500' : 'bg-red-500'} animate-pulse`}></div>
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900">{service.name}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(service.status)}`}>
                                <i className={`${getStatusIcon(service.status)} mr-1`}></i>
                                {getStatusText(service.status)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-8">
                          <div className="text-center min-w-[80px]">
                            <p className="text-sm text-gray-500 mb-1">正常运行时间</p>
                            <p className="text-lg font-semibold text-gray-900">{service.uptime}</p>
                          </div>
                          <div className="text-center min-w-[80px]">
                            <p className="text-sm text-gray-500 mb-1">响应时间</p>
                            <p className="text-lg font-semibold text-gray-900">{service.responseTime}</p>
                          </div>
                          <div className="text-center min-w-[80px]">
                            <p className="text-sm text-gray-500 mb-1">最后检查</p>
                            <p className="text-sm text-gray-600">{service.lastChecked}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Uptime History */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">过去90天正常运行时间</h2>
          
          <div className="bg-white rounded-xl shadow-md p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-2xl font-bold text-gray-900">99.97%</p>
                <p className="text-gray-600">平均正常运行时间</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                  <span className="text-sm text-gray-600">正常</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                  <span className="text-sm text-gray-600">降级</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-500 rounded"></div>
                  <span className="text-sm text-gray-600">中断</span>
                </div>
              </div>
            </div>

            <div className="flex items-end gap-1 h-32">
              {Array.from({ length: 90 }).map((_, index) => (
                <div 
                  key={index}
                  className="flex-1 bg-green-500 rounded-t hover:bg-green-600 transition-colors cursor-pointer"
                  style={{ height: `${95 + Math.random() * 5}%` }}
                  title={`第 ${90 - index} 天: 99.${Math.floor(95 + Math.random() * 5)}%`}
                ></div>
              ))}
            </div>

            <div className="flex justify-between mt-4 text-sm text-gray-500">
              <span>90天前</span>
              <div className="text-center">
                <div className="font-semibold text-gray-900">今天</div>
                <div className="text-teal-600 mt-1">
                  {currentTime.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Incident History */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">事件历史</h2>
          
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-checkbox-circle-line text-4xl text-green-600"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">无事件记录</h3>
            <p className="text-gray-600">
              过去90天内没有发生任何服务中断或性能降级事件
            </p>
          </div>
        </div>
      </section>

      {/* Subscription Section */}
      <section className="py-20 bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <i className="ri-notification-3-line text-3xl text-white"></i>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">订阅状态更新</h2>
          <p className="text-lg text-gray-600 mb-8">
            订阅系统状态通知，第一时间了解系统异常事件和维护计划
          </p>
          <button 
            onClick={() => setShowSubscriptionModal(true)}
            className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
          >
            订阅
          </button>
        </div>
      </section>

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 relative animate-fadeIn">
            <button
              onClick={() => setShowSubscriptionModal(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4">
              <i className="ri-mail-line text-2xl text-white"></i>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-2">订阅状态通知</h3>
            <p className="text-gray-600 mb-6">填写您的邮箱地址，选择您想接收的通知类型</p>

            <form id="status-subscription-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  邮箱地址 <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  通知类型
                </label>
                <div className="space-y-3">
                  <label className="flex items-start cursor-pointer group">
                    <input
                      type="checkbox"
                      name="incidents"
                      checked={formData.notifications.incidents}
                      onChange={() => handleCheckboxChange('incidents')}
                      className="mt-1 w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                    />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900 group-hover:text-teal-600 transition-colors">
                        系统异常事件
                      </div>
                      <div className="text-xs text-gray-500">
                        当系统出现故障或异常时通知我
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start cursor-pointer group">
                    <input
                      type="checkbox"
                      name="maintenance"
                      checked={formData.notifications.maintenance}
                      onChange={() => handleCheckboxChange('maintenance')}
                      className="mt-1 w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                    />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900 group-hover:text-teal-600 transition-colors">
                        计划维护通知
                      </div>
                      <div className="text-xs text-gray-500">
                        提前通知我系统维护计划
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start cursor-pointer group">
                    <input
                      type="checkbox"
                      name="updates"
                      checked={formData.notifications.updates}
                      onChange={() => handleCheckboxChange('updates')}
                      className="mt-1 w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                    />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900 group-hover:text-teal-600 transition-colors">
                        功能更新通知
                      </div>
                      <div className="text-xs text-gray-500">
                        接收新功能和改进的通知
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-emerald-600 text-xl"></i>
                  <span className="text-sm text-emerald-700 font-medium">订阅成功！我们会将状态更新发送到您的邮箱</span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                  <i className="ri-error-warning-fill text-red-600 text-xl"></i>
                  <span className="text-sm text-red-700 font-medium">订阅失败，请稍后重试</span>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowSubscriptionModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !formData.email}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? '提交中...' : '确认订阅'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
